/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import {IconButton} from '@mui/material';
import {useEffect, useState} from 'react';
import {selectCoverageRequirementsClipboard, setCoverageRequirementsClipboard} from "../../../../../../../../redux/ducks/CoverageRequirements/slice";
import {useAppDispatch, useAppSelector} from "../../../../../../../../redux/hooks";
import ContextMenu from '../../../../../../../components/ContextMenu/ArgosContextMenu';
import RedlineAppDialog from '../../../../../../../components/Dialogs/RedlineAppDialogs/RedlineAppDialog';
import {REDLINE_DIALOG_OPTIONS, TREE_OPERATIONS_SWITCH_OPTIONS} from "../../../../constants";
import {getNodeById, ITreeNode} from './CoverageRequirementsTreeComponent';

interface IContextMenuOption {
  name: string,
  type: string
}

interface IContextMenuProps {
  contextMenuOptions: IContextMenuOption[]
  isTreeNodeContextMenuOpen: boolean
  setIsTreeNodeContextMenuOpen: any
  contextMenuTopLeftCoordinates: any
  isContextMenuTreeNodeDataset: boolean

  // RedlineAppDialog
  handleOpenRedlineAppDialog: any
  isRedlineAppDialogOpen: boolean
  redlineAppDialogTitle: string
  redlineAppDialogType: string
  handleCloseRedlineAppDialog: any
  treeData: ITreeNode
  treeOperation: any

  contextNodeId: any
  contextTreeId: any

  treeType: string
}

const TreeItemContextMenu = (props: IContextMenuProps) => {
  const dispatch = useAppDispatch()
  const {
    // Context menu
    contextMenuOptions,
    isTreeNodeContextMenuOpen,
    setIsTreeNodeContextMenuOpen,
    contextMenuTopLeftCoordinates,
    isContextMenuTreeNodeDataset,

    // RedlineAppDialog
    handleOpenRedlineAppDialog,
    isRedlineAppDialogOpen,
    redlineAppDialogTitle,
    redlineAppDialogType,
    handleCloseRedlineAppDialog,
    treeData,

    treeOperation,
    contextNodeId,
    contextTreeId,
  } = props

  const coverageRequirementsClipboard = useAppSelector(state => selectCoverageRequirementsClipboard(state))
  const isClipboardFull = coverageRequirementsClipboard !== null
  // for contextMenu options, do not show Paste Contents if there is nothing to paste
  const shouldShowPasteOptionInContextMenu = (option: IContextMenuOption): boolean => {
    return option.type !== TREE_OPERATIONS_SWITCH_OPTIONS.PASTE_CONTENTS || (option.type === TREE_OPERATIONS_SWITCH_OPTIONS.PASTE_CONTENTS && isClipboardFull)
  }
  const [_contextMenuOptions, setContextMenuOptions]: any[] = useState([])

  useEffect(() => {
    setContextMenuOptions(contextMenuOptions.filter((contextMenuOption: IContextMenuOption) => shouldShowPasteOptionInContextMenu(contextMenuOption)))
  }, [props])

  const shouldShowContextMenu = isTreeNodeContextMenuOpen && contextMenuOptions.length !== 0
  const addChildrenToNodesList = (nodeId: string, nodeList: string[], treeData: any): void => {
    const node = getNodeById(treeData, nodeId)
    if (node === null)
      return
    node?.children?.forEach((childNode: ITreeNode) => {
      nodeList.push(childNode?.nodeId)
      if (childNode.nodeType === "folder")
        addChildrenToNodesList(childNode?.nodeId, nodeList, treeData)
    })

  }
  const handleCopyOperations = (operation: string) => {
    if (operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET || operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET_TO_STUDY) {
      props?.treeOperation(operation, {"nodeToCopy": contextNodeId, "isSingleCopy": true})
    } else if (operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER || operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER_TO_STUDY) {
      const contextNodeIds: string[] = []
      contextNodeIds.push(contextNodeId)
      addChildrenToNodesList(contextNodeId, contextNodeIds, treeData)
      dispatch(setCoverageRequirementsClipboard({"nodeToCopy": contextNodeIds}))
      props?.treeOperation(operation, {"nodeToCopy": contextNodeIds.length === 1 ? contextNodeId : contextNodeIds, "isSingleCopy": contextNodeIds.length === 1})
    } else if (operation === TREE_OPERATIONS_SWITCH_OPTIONS.PASTE_CONTENTS) {
      props?.treeOperation(operation, {"destinationNode": contextNodeId})
    }
  }
  const isCopyOperation = (operation: string) => {
    return operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET ||
      operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER ||
      operation === TREE_OPERATIONS_SWITCH_OPTIONS.PASTE_CONTENTS ||
      operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER_TO_STUDY ||
      operation === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET_TO_STUDY
  }

  /**
   * Recursive function that given the node id and tree data, checks all children recursively and adds each dataset node to a list and returns the list
   * @param nodeId
   * @param treeData
   */
  const getDatasetNodes = (nodeId: string, treeData: any): any[] => {
    const node = getNodeById(treeData, nodeId);
    if (node === null) return [];

    if (node.nodeType === "dataset") {
      return [node];
    }

    let datasetNodes: any[] = [];
    node?.children?.forEach((childNode: ITreeNode) => {
      if (childNode.nodeType === "dataset") {
        datasetNodes.push(childNode);
      }
      if (childNode.nodeType === "folder" || childNode.nodeType === "study") {
        datasetNodes = datasetNodes.concat(getDatasetNodes(childNode?.nodeId, treeData));
      }
    });
    return datasetNodes;
  };

  const dialogData = redlineAppDialogType === REDLINE_DIALOG_OPTIONS.DELETE
    ? {nodes: getDatasetNodes(contextNodeId, treeData)} : undefined

  return (
    <>
      {shouldShowContextMenu && (
        <ContextMenu top={contextMenuTopLeftCoordinates.y} left={contextMenuTopLeftCoordinates.x}>
          <ul>
            {
              _contextMenuOptions.map((element: IContextMenuOption, index: any) =>
                <li
                  key={index}
                  onClick={e => {
                    setIsTreeNodeContextMenuOpen(false);
                    if (!isCopyOperation(element.type))
                      handleOpenRedlineAppDialog(element.type, element.name)
                    else
                      handleCopyOperations(element.type)
                  }}
                >
                  {element.name}
                </li>
              )
            }
          </ul>
          <IconButton
            style={{justifyContent: "right", color: "white"}}
            onClick={e => setIsTreeNodeContextMenuOpen(false)}
          >
            <DisabledByDefaultIcon/>
          </IconButton>
        </ContextMenu>
      )
      }
      {
        isRedlineAppDialogOpen &&
        <RedlineAppDialog isdataset={isContextMenuTreeNodeDataset} action={treeOperation}
                          dialogtitle={redlineAppDialogTitle}
                          dialogtype={redlineAppDialogType}
                          treeData={treeData}
                          open={isRedlineAppDialogOpen}
                          onClose={() => handleCloseRedlineAppDialog()}
                          contextNodeId={contextNodeId}
                          contextTreeId={contextTreeId}
                          treeType={props.treeType}
                          data={dialogData}
        />
      }
    </>
  )
}
export default TreeItemContextMenu;
